import React, { useEffect, useState } from "react";
import { Calculator, Loader2, Target, Star, Flame, Coins, Copy, Check, TrendingUp, Snowflake, Hash } from "lucide-react";
import axios from "axios";
import { tauroAnalyze } from "../tauroApi";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_OPTIONS = [
  "Lotería Nacional", "Leidsa", "Lotería Real", "Loteka", "La Primera",
  "La Suerte Dominicana", "LoteDom",
  "Anguila Lottery (10AM)", "Anguila Lottery (1PM)", "Anguila Lottery (6PM)", "Anguila Lottery (9PM)",
  "King Lottery (7:30PM)", "King Lottery (12:30AM)",
  "Florida Día", "Florida Noche", "New York Día", "New York Noche",
];

const PLAY_TYPES = [
  { id: "quiniela", name: "Quiniela" },
  { id: "pale", name: "Palé" },
  { id: "tripleta", name: "Tripleta" },
  { id: "super-pale", name: "Súper Palé" },
];

export default function TauroAnalyzer({ onBuyCredits }) {
  const [lotteryOptions, setLotteryOptions] = useState(FALLBACK_OPTIONS);
  const [lottery, setLottery] = useState(FALLBACK_OPTIONS[0]);
  const [playType, setPlayType] = useState(PLAY_TYPES[0].id);
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/tauro/lotteries`)
      .then((r) => {
        const names = (r.data?.lotteries || []).map((l) => l.name);
        if (names.length > 0) setLotteryOptions(names);
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const r = await tauroAnalyze(lottery, playType, context || null);
      setResult(r.analysis);
      refreshUser();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error generando an\u00e1lisis";
      if (err?.response?.status === 402) {
        toast({ title: "Sin cr\u00e9ditos", description: detail });
        onBuyCredits && onBuyCredits();
      } else {
        toast({ title: "Error", description: detail });
      }
    } finally {
      setLoading(false);
    }
  };

  const copyResult = async () => {
    if (!result) return;
    const text = `Tauro recomienda para ${result.lottery} (${result.play_type}):\nN\u00fameros: ${(result.numbers || []).join(", ")}\nFuerte: ${result.strong}\nPal\u00e9: ${result.pale}\nConfianza: ${result.confidence}%\n${result.reasoning}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({ title: "Copiado" });
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Form */}
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-md border border-blue-100 p-6">
        <h3 className="font-extrabold text-lg text-[#0e4a7a] flex items-center gap-2 mb-4">
          <Calculator className="w-5 h-5" /> Generador de recomendaciones
        </h3>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs font-semibold text-gray-700">Lotería</span>
            <select
              value={lottery}
              onChange={(e) => setLottery(e.target.value)}
              className="mt-1 w-full bg-blue-50/60 border-2 border-blue-100 focus:border-[#1d6cb1] rounded-xl px-3 py-2 text-sm outline-none transition"
            >
              {lotteryOptions.map((l) => <option key={l}>{l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-gray-700">Tipo de jugada</span>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {PLAY_TYPES.map((pt) => (
                <button
                  type="button"
                  key={pt.id}
                  onClick={() => setPlayType(pt.id)}
                  className={`px-3 py-2 rounded-xl text-sm font-semibold border-2 transition ${
                    playType === pt.id
                      ? "bg-[#1d6cb1] border-[#1d6cb1] text-white"
                      : "bg-white border-blue-100 text-gray-700 hover:border-[#1d6cb1]"
                  }`}
                >
                  {pt.name}
                </button>
              ))}
            </div>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-gray-700">Contexto (opcional)</span>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Ej: 'So\u00f1\u00e9 con un caballo', 'Mi fecha de cumplea\u00f1os es 15 de agosto'..."
              className="mt-1 w-full bg-blue-50/60 border-2 border-blue-100 focus:border-[#1d6cb1] rounded-xl px-3 py-2 text-sm outline-none transition resize-none"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white font-bold shadow hover:shadow-lg disabled:opacity-60 transition"
          >
            {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Tauro analiza...</>) : (<><Calculator className="w-4 h-4" /> Generar análisis (2 créditos)</>)}
          </button>
          <p className="text-[11px] text-gray-500 text-center inline-flex items-center justify-center gap-1 w-full">
            <Coins className="w-3 h-3" /> Tus créditos: {user?.credits ?? 0}
          </p>
        </div>
      </form>

      {/* Result */}
      <div className="bg-white rounded-2xl shadow-md border border-blue-100 p-6">
        <h3 className="font-extrabold text-lg text-[#0e4a7a] flex items-center gap-2 mb-4">
          <Star className="w-5 h-5 text-amber-500" /> Recomendación de Tauro
        </h3>
        {!result && !loading && (
          <p className="text-sm text-gray-500 text-center py-10">
            Selecciona una lotería y tipo de jugada, luego haz clic en “Generar”.
          </p>
        )}
        {loading && (
          <div className="flex items-center justify-center py-10 text-sm text-gray-500 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Tauro calcula la mejor recomendación...
          </div>
        )}
        {result && (
          <div className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Números recomendados</p>
              <div className="flex flex-wrap gap-2">
                {(result.numbers || []).map((n, i) => (
                  <span key={i} className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white font-extrabold text-xl shadow-md" style={{fontFamily:'Playfair Display, serif'}}>
                    {n}
                  </span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-gray-500">Fuerte</p>
                <p className="text-xl font-extrabold text-[#0e4a7a]" style={{fontFamily:'Playfair Display, serif'}}>{result.strong}</p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-gray-500">Palé</p>
                <p className="text-xl font-extrabold text-[#0e4a7a]" style={{fontFamily:'Playfair Display, serif'}}>{result.pale}</p>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1 inline-flex items-center gap-1">
                <Flame className="w-3 h-3 text-orange-500" /> Confianza
              </p>
              <div className="w-full h-2 bg-blue-50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-orange-500"
                  style={{ width: `${Math.min(100, Math.max(0, result.confidence || 0))}%` }}
                />
              </div>
              <p className="text-xs text-gray-600 mt-1">{result.confidence}% estadística de éxito estimada</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1 inline-flex items-center gap-1">
                <Target className="w-3 h-3 text-[#1d6cb1]" /> Análisis
              </p>
              <p className="text-sm text-gray-700 leading-relaxed bg-blue-50/50 border border-blue-100 rounded-xl p-3">
                {result.reasoning}
              </p>
            </div>
            {result.tips && result.tips.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Consejos</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  {result.tips.map((t, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-amber-500">•</span> <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.stats_summary && (
              <div className="border-t border-blue-100 pt-3">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 inline-flex items-center gap-1">
                  <Hash className="w-3 h-3" /> Base estadística ({result.stats_summary.draws_analyzed} sorteos analizados)
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {result.stats_summary.hot?.length > 0 && (
                    <div className="bg-orange-50 border border-orange-100 rounded-lg p-2">
                      <p className="font-bold text-orange-700 mb-1 inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> HOT (frecuentes)</p>
                      <div className="flex flex-wrap gap-1">
                        {result.stats_summary.hot.map((n) => (
                          <span key={n} className="px-1.5 py-0.5 bg-white border border-orange-200 rounded font-bold text-orange-700">{n}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {result.stats_summary.cold?.length > 0 && (
                    <div className="bg-sky-50 border border-sky-100 rounded-lg p-2">
                      <p className="font-bold text-sky-700 mb-1 inline-flex items-center gap-1"><Snowflake className="w-3 h-3" /> COLD (rezagados)</p>
                      <div className="flex flex-wrap gap-1">
                        {result.stats_summary.cold.map((n) => (
                          <span key={n} className="px-1.5 py-0.5 bg-white border border-sky-200 rounded font-bold text-sky-700">{n}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {result.stats_summary.top_pales_history?.length > 0 && (
                    <div className="bg-purple-50 border border-purple-100 rounded-lg p-2 sm:col-span-2">
                      <p className="font-bold text-purple-700 mb-1">Palés que han salido juntos</p>
                      <div className="flex flex-wrap gap-1">
                        {result.stats_summary.top_pales_history.map((p) => (
                          <span key={p} className="px-1.5 py-0.5 bg-white border border-purple-200 rounded font-bold text-purple-700">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {result.llm_powered === false && (
                  <p className="text-[11px] text-gray-400 mt-2 italic">
                    Análisis 100% estadístico (motor de IA temporalmente saturado).
                  </p>
                )}
              </div>
            )}
            <button
              onClick={copyResult}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copiado" : "Copiar análisis"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
